import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { toast } from "sonner";
import { Loader2, CheckCircle2, MailCheck } from "lucide-react";
import { TIMEZONES } from "@/lib/timezones";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { brand: branding } = useBranding() || { brand: {} };
  const [state, setState] = useState({ loading: true, invite: null, error: null });
  const [form, setForm] = useState({ first_name: "", discord_name: "", password: "", title: "", department: "", job: "", location: "", timezone: "", start_date: "", birthdate: "", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/invites/verify/${token}`);
        if (!mounted) return;
        setState({ loading: false, invite: data, error: null });
        setForm((f) => ({ ...f, title: data.title || "", department: data.department || "", job: data.job || "", location: data.location || "", timezone: data.timezone || (Intl.DateTimeFormat().resolvedOptions().timeZone || "") }));
      } catch (e) {
        if (!mounted) return;
        setState({ loading: false, invite: null, error: formatApiError(e) });
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() && !form.discord_name.trim()) {
      toast.error("Please enter your first name or discord name");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/invites/accept", { token, ...form });
      localStorage.setItem("np_token", data.token);
      await refresh();
      toast.success(`Welcome to ${state.invite.brand}!`);
      navigate("/welcome", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0a0d12] text-slate-300">
        <Loader2 className="animate-spin" size={28} />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0a0d12] text-slate-300 px-6">
        <div className="max-w-md text-center border border-slate-800 rounded-2xl p-8 bg-[#101821]">
          <MailCheck size={40} className="mx-auto mb-4 text-[#ff3b5c]" />
          <h1 className="text-2xl font-black mb-2">Invitation issue</h1>
          <p className="text-sm text-slate-400 mb-6">{state.error}</p>
          <button
            data-testid="invite-back-login"
            onClick={() => navigate("/login")}
            className="px-5 py-2 rounded-lg bg-[color:var(--accent)] text-black font-bold text-sm"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  const inv = state.invite;
  return (
    <div className="min-h-screen bg-[#0a0d12] text-slate-200 grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 bg-[#101821] border-r border-slate-800">
        <div className="flex items-center gap-3">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt={inv.brand} className="w-10 h-10 rounded-lg object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-lg grid place-items-center font-black text-black" style={{ background: branding.accent_color || "var(--accent)" }}>
              {(inv.brand || "N").charAt(0)}
            </div>
          )}
          <div>
            <div className="font-black text-lg">{inv.brand}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500">{branding.tagline}</div>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--accent)] mb-3">You're invited</div>
          <h1 className="text-4xl font-black leading-tight mb-4">
            Join the team.
            <br />
            <span style={{ color: branding.accent_color || "var(--accent)" }}>Get to work.</span>
          </h1>
          <p className="text-slate-400 max-w-md">
            Your admin has set you up as <strong className="text-slate-200">{inv.role}</strong>. Set your password and you'll land on your
            dashboard.
          </p>
        </div>
        <div className="text-xs text-slate-600">© {inv.brand} · Secure onboarding</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={accept} className="w-full max-w-md space-y-4" data-testid="invite-form">
          <div>
            <h2 className="text-2xl font-black">Complete your account</h2>
            <p className="text-sm text-slate-400 mt-1">Invited: <span className="text-slate-200">{inv.email}</span></p>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-emerald-900/60 bg-emerald-900/10 text-emerald-300 px-3 py-2 text-xs">
            <CheckCircle2 size={14} /> Invitation verified · Role: <span className="font-semibold">{inv.role}</span>
          </div>

          <Field label="First name" required>
            <input data-testid="invite-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required className="input" />
          </Field>
          <Field label="Discord name">
            <input data-testid="invite-discord-name" value={form.discord_name} onChange={(e) => setForm({ ...form, discord_name: e.target.value })} placeholder="e.g. jonvard_" className="input" />
          </Field>
          <Field label="Password (min 8 chars)" required>
            <input data-testid="invite-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} className="input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Job title">
              <input data-testid="invite-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" />
            </Field>
            <Field label="Department">
              <input data-testid="invite-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="input" />
            </Field>
          </div>
          <Field label="Job">
            <input data-testid="invite-job" value={form.job} onChange={(e) => setForm({ ...form, job: e.target.value })} placeholder="e.g. Stream Manager, Community Mod" className="input" />
          </Field>
          <Field label="Location">
            <input data-testid="invite-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Austin, TX / Remote" className="input" />
          </Field>
          <Field label="Timezone">
            <select data-testid="invite-timezone" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} className="input">
              <option value="">Not set</option>
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date">
              <input type="date" data-testid="invite-start-date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input" />
            </Field>
            <Field label="Birthday">
              <input type="date" data-testid="invite-birthdate" value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} className="input" />
            </Field>
          </div>
          <Field label="Phone (optional)">
            <input data-testid="invite-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" />
          </Field>

          <button
            data-testid="invite-submit"
            type="submit"
            disabled={busy}
            className="w-full py-3 rounded-lg font-bold text-black disabled:opacity-60"
            style={{ background: branding.accent_color || "var(--accent)" }}
          >
            {busy ? "Creating account…" : "Accept & sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">
        {label}
        {required && <span className="text-[#ff3b5c] ml-1">*</span>}
      </div>
      {children}
    </label>
  );
}
